import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react"

export type Theme = "demon" | "fairy"

type ThemeContextValue = {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  isFairy: boolean
  isTransitioning: boolean
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const DOCUMENT_IDENTITY = {
  demon: {
    title: "Sales Demon | AI Invoice Follow-Up",
    favicon: "/assets/brand/demon-logo.png",
    themeColor: "#0a0a0b",
  },
  fairy: {
    title: "Sales Fairy | AI Invoice Follow-Up",
    favicon: "/assets/brand/fairy-logo.png",
    themeColor: "#f8fafc",
  },
} satisfies Record<Theme, { title: string; favicon: string; themeColor: string }>

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "demon"
  try {
    const stored = window.localStorage.getItem("demon-app.theme")
    return stored === "fairy" ? "fairy" : "demon"
  } catch {
    return "demon"
  }
}

function applyThemeToDocument(theme: Theme) {
  if (typeof document === "undefined") return
  const body = document.body
  if (!body) return
  const identity = DOCUMENT_IDENTITY[theme]
  body.classList.remove("theme-demon", "theme-fairy")
  body.classList.add(`theme-${theme}`)
  body.dataset.theme = theme
  body.style.colorScheme = theme === "fairy" ? "light" : "dark"
  document.title = identity.title
  document.querySelector<HTMLLinkElement>('link[rel~="icon"]')?.setAttribute("href", identity.favicon)
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", identity.themeColor)
}

export function initializeStoredTheme() {
  const theme = getStoredTheme()
  applyThemeToDocument(theme)
  return theme
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    applyThemeToDocument(theme)

    try {
      window.localStorage.setItem("demon-app.theme", theme)
    } catch {
      // The active theme still applies when browser storage is unavailable.
    }
  }, [theme])

  const setTheme = useCallback((newTheme: Theme) => {
    if (newTheme === theme) return
    
    setIsTransitioning(true)
    
    // Brief transition effect
    setTimeout(() => {
      setThemeState(newTheme)
      
      setTimeout(() => {
        setIsTransitioning(false)
      }, 300)
    }, 100)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme(theme === "fairy" ? "demon" : "fairy")
  }, [theme, setTheme])

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isFairy: theme === "fairy",
      isTransitioning,
    }),
    [theme, setTheme, toggleTheme, isTransitioning],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider")
  }
  return ctx
}
